import { ChevronRight } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Tag } from '@/components/ui/Tag'
import { useFormat } from '@/i18n/useFormat'
import { cn } from '@/lib/cn'
import { formatMoney, formatNumber } from '@/lib/format'
import { unitLabel } from '@/lib/legUnits'
import type {
  CounterpartyStatementItem,
  CounterpartyStatementRow,
  CounterpartyStatementTotals,
  Money,
  Settlement,
} from '@/types/domain'

/* «Выписка» — one counterparty's works for the year (S-22e,
 * `direction-ac2-final.png`): one row per work oldest first, its units, what
 * the counterparty billed, what we paid, the «Остаток» and the running balance
 * carried down the page, with the opening strip above and «Итого» under them.
 * Rows are NOT sortable, for the client statement's reason: `running` is a
 * balance accumulated in this order.
 *
 * A row expands into what the work holds of the counterparty: its units with
 * the sum and payment state of each, «Плечо N целиком», «Прочие затраты» and
 * «Аванс, не разнесено». Every figure is the server's; nothing is added here.
 *
 * Hand-built rather than on DataTable, because DataTable has no expandable row.
 * Below `md` the table becomes stacked cards (`md:hidden`) — the RecordList
 * arrangement — so a 390 px phone never scrolls sideways. The money columns,
 * the strips and every amount in the expansion exist only when the server sent
 * `opening` and `totals` (the page's reading of `moneyUnavailable`). */

/** The tone each served payment state wears — the cost table's
 *  (`ExpenseTable`'s `SETTLEMENT_TONES`): never brand for a finished state, and
 *  always beside its word, so colour never carries it alone. */
const SETTLEMENT_TONES: Record<Settlement, 'neutral' | 'attention' | 'positive'> = {
  unpaid: 'neutral',
  'partly-paid': 'attention',
  paid: 'positive',
}

const STRIP_ROW = 'border-line-hair flex items-center justify-between gap-4 border-b px-[18px] py-2.5 text-[13px]'

function routeText(row: CounterpartyStatementRow): string {
  return [row.route.loading, row.route.border, row.route.discharge, row.route.unloading]
    .filter((part) => part.trim() !== '')
    .join(' → ')
}

/** A served amount in a column, grouped; a negative one (an overpaid work)
 *  says so in colour AND with its minus sign. */
function Amount({ value, emphasis = false }: { value: Money; emphasis?: boolean }) {
  return (
    <span
      className={cn(
        'asa-num',
        emphasis && 'font-semibold',
        value.amount.trim().startsWith('-') && 'text-negative',
      )}
    >
      {formatNumber(value.amount, 2)}
    </span>
  )
}

export function CounterpartyStatementTable({
  rows,
  opening,
  totals,
}: {
  rows: CounterpartyStatementRow[]
  opening?: Money
  totals?: CounterpartyStatementTotals
}) {
  const { t } = useTranslation()
  const format = useFormat()
  const showMoney = opening !== undefined && totals !== undefined

  /* Collapsed by default and not persisted, like the cost table's groups:
     every visit starts from the works, not from the last reader's unfolding. */
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set())
  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (!next.delete(id)) next.add(id)
      return next
    })

  const head = 'asa-eyebrow h-9 px-3'
  const cell = 'h-[38px] px-3 text-[13px]'
  const columnCount = showMoney ? 9 : 5

  const toggleButton = (row: CounterpartyStatementRow) =>
    row.items.length > 0 ? (
      <button
        type="button"
        aria-expanded={expanded.has(row.caseId)}
        aria-label={t('counterparties.statement.toggle', { number: row.number })}
        onClick={() => toggle(row.caseId)}
        className="text-text-muted hover:text-text-heading inline-flex cursor-pointer items-center"
      >
        <ChevronRight
          size={14}
          aria-hidden
          className={cn('transition-transform', expanded.has(row.caseId) && 'rotate-90')}
        />
      </button>
    ) : null

  const numberLink = (row: CounterpartyStatementRow) => (
    <Link to={`/cases/${row.caseId}`} className="text-brand text-[13px] font-semibold">
      {row.number}
    </Link>
  )

  return (
    <>
      {showMoney ? (
        <div className={STRIP_ROW} data-testid="statement-opening">
          <span className="text-text-muted">{t('counterparties.statement.openingRow')}</span>
          <Amount value={opening} emphasis />
        </div>
      ) : null}

      {/* The desk shape. `relative`: a scroll box around a table is positioned,
          or its sr-only header escapes and widens the document (frontend/README). */}
      <div className="asa-scroll relative hidden overflow-x-auto md:block">
        <table
          aria-label={t('counterparties.statement.tableLabel')}
          className="w-full border-collapse text-left"
        >
          <thead>
            <tr className="border-line border-b">
              {/* The chevrons' column: each button names itself. */}
              <th scope="col" className="h-9 w-9 pl-[18px]" />
              <th scope="col" className={head}>{t('counterparties.statement.columns.number')}</th>
              <th scope="col" className={head}>{t('counterparties.statement.columns.openedOn')}</th>
              <th scope="col" className={head}>{t('counterparties.statement.columns.route')}</th>
              <th scope="col" className={cn(head, 'text-right')}>
                {t('counterparties.statement.columns.units')}
              </th>
              {showMoney ? (
                <>
                  <th scope="col" className={cn(head, 'text-right')}>
                    {t('counterparties.statement.columns.billed')}
                  </th>
                  <th scope="col" className={cn(head, 'text-right')}>
                    {t('counterparties.statement.columns.paid')}
                  </th>
                  <th scope="col" className={cn(head, 'text-right')}>
                    {t('counterparties.statement.columns.balance')}
                  </th>
                  <th scope="col" className={cn(head, 'pr-[18px] text-right')}>
                    {t('counterparties.statement.columns.running')}
                  </th>
                </>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <StatementRowGroup
                key={row.caseId}
                open={expanded.has(row.caseId)}
                columnCount={columnCount}
                items={row.items}
                number={row.number}
              >
                <td className="h-[38px] w-9 pl-[18px]">{toggleButton(row)}</td>
                <td className={cell}>{numberLink(row)}</td>
                <td className={cn(cell, 'whitespace-nowrap')}>{format.date(row.openedOn)}</td>
                <td className={cell}>{routeText(row) || t('common.empty')}</td>
                <td className={cn(cell, 'asa-num text-right')}>{row.units}</td>
                {showMoney ? (
                  row.money ? (
                    <>
                      <td className={cn(cell, 'text-right')}><Amount value={row.money.billed} /></td>
                      <td className={cn(cell, 'text-right')}><Amount value={row.money.paid} /></td>
                      <td className={cn(cell, 'text-right')}><Amount value={row.money.balance} /></td>
                      <td className={cn(cell, 'pr-[18px] text-right')}>
                        <Amount value={row.money.running} emphasis />
                      </td>
                    </>
                  ) : (
                    <td colSpan={4} />
                  )
                ) : null}
              </StatementRowGroup>
            ))}
          </tbody>
          {showMoney ? (
            <tfoot>
              <tr className="border-line border-t" data-testid="statement-total">
                <td />
                <th scope="row" colSpan={3} className={cn(cell, 'text-text-heading text-left font-semibold')}>
                  {t('counterparties.statement.totalRow')}
                </th>
                <td className={cn(cell, 'asa-num text-right font-semibold')}>{totals.units}</td>
                <td className={cn(cell, 'text-right')}><Amount value={totals.billed} emphasis /></td>
                <td className={cn(cell, 'text-right')}><Amount value={totals.paid} emphasis /></td>
                <td className={cell} />
                <td className={cn(cell, 'pr-[18px] text-right')}>
                  <Amount value={totals.balance} emphasis />
                </td>
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>

      {/* The phone shape: one card per work, its figures stacked, the same
          expansion under it. */}
      <ul className="flex flex-col md:hidden" aria-label={t('counterparties.statement.tableLabel')}>
        {rows.map((row, index) => (
          <li
            key={row.caseId}
            className={cn('flex flex-col gap-2 px-4 py-3', index !== rows.length - 1 && 'border-line-hair border-b')}
          >
            <div className="flex items-center gap-2">
              {numberLink(row)}
              <span className="text-text-muted text-[12px] font-light">{format.date(row.openedOn)}</span>
              <span className="flex-1" />
              {toggleButton(row)}
            </div>
            <span className="text-text-heading text-[13.5px]">{routeText(row) || t('common.empty')}</span>
            <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 text-[13px]">
              <dt className="text-text-muted">{t('counterparties.statement.columns.units')}</dt>
              <dd className="asa-num text-right">{row.units}</dd>
              {row.money ? (
                <>
                  <dt className="text-text-muted">{t('counterparties.statement.columns.billed')}</dt>
                  <dd className="text-right"><Amount value={row.money.billed} /></dd>
                  <dt className="text-text-muted">{t('counterparties.statement.columns.paid')}</dt>
                  <dd className="text-right"><Amount value={row.money.paid} /></dd>
                  <dt className="text-text-muted">{t('counterparties.statement.columns.balance')}</dt>
                  <dd className="text-right"><Amount value={row.money.balance} /></dd>
                  <dt className="text-text-muted">{t('counterparties.statement.columns.running')}</dt>
                  <dd className="text-right"><Amount value={row.money.running} emphasis /></dd>
                </>
              ) : null}
            </dl>
            {expanded.has(row.caseId) ? <ItemList items={row.items} number={row.number} /> : null}
          </li>
        ))}
      </ul>

      {showMoney ? (
        <dl
          className="border-line grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 border-t px-4 py-3 text-[13px] md:hidden"
          data-testid="statement-total-phone"
        >
          <dt className="text-text-heading font-semibold">{t('counterparties.statement.totalRow')}</dt>
          <dd />
          <dt className="text-text-muted">{t('counterparties.statement.columns.billed')}</dt>
          <dd className="text-right"><Amount value={totals.billed} emphasis /></dd>
          <dt className="text-text-muted">{t('counterparties.statement.columns.paid')}</dt>
          <dd className="text-right"><Amount value={totals.paid} emphasis /></dd>
          <dt className="text-text-muted">{t('counterparties.money.tiles.balance')}</dt>
          <dd className="text-right"><Amount value={totals.balance} emphasis /></dd>
        </dl>
      ) : null}
    </>
  )
}

/** A work's row and, when it is open, the row under it holding the expansion. */
function StatementRowGroup({
  open,
  columnCount,
  items,
  number,
  children,
}: {
  open: boolean
  columnCount: number
  items: CounterpartyStatementItem[]
  number: string
  children: ReactNode
}) {
  return (
    <>
      <tr className="border-line-hair hover:bg-surface-hover border-b">{children}</tr>
      {open ? (
        <tr className="border-line-hair border-b">
          <td />
          <td colSpan={columnCount - 1} className="pr-[18px] pb-2">
            <ItemList items={items} number={number} />
          </td>
        </tr>
      ) : null}
    </>
  )
}

/** What the work holds of the counterparty, one line per item. */
function ItemList({ items, number }: { items: CounterpartyStatementItem[]; number: string }) {
  const { t } = useTranslation()
  return (
    <ul
      aria-label={t('counterparties.statement.toggle', { number })}
      className="bg-surface-sunken flex flex-col rounded-[var(--radius-DEFAULT)]"
    >
      {items.map((item, index) => {
        const amount = item.kind === 'advance' ? item.amount : item.billed
        return (
          <li
            key={itemKey(item, index)}
            className={cn(
              'grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-0.5 px-3 py-2 text-[13px]',
              'sm:grid-cols-[minmax(0,1fr)_auto_8rem]',
              index !== items.length - 1 && 'border-line-hair border-b',
            )}
          >
            <ItemName item={item} />
            <span className="asa-num text-right whitespace-nowrap">
              {amount ? formatMoney(amount.amount, amount.currency) : null}
            </span>
            <span className="col-span-2 sm:col-span-1">
              <SettlementTag settlement={'settlement' in item ? item.settlement : undefined} />
            </span>
          </li>
        )
      })}
    </ul>
  )
}

function itemKey(item: CounterpartyStatementItem, index: number): string {
  switch (item.kind) {
    case 'unit':
      return `unit:${item.unitId}`
    case 'leg':
      return `leg:${item.legId}`
    default:
      return `${item.kind}:${index}`
  }
}

function ItemName({ item }: { item: CounterpartyStatementItem }) {
  const { t } = useTranslation()
  switch (item.kind) {
    case 'unit':
      return (
        <span className="flex min-w-0 flex-wrap items-baseline gap-x-2">
          <span className="text-text-heading font-semibold break-words">
            {unitLabel(t, {
              kind: item.unitKind,
              number: item.number.trim() || t('counterparties.statement.items.noNumber'),
            })}
          </span>
          <span className="text-text-muted text-[12px] font-light">
            {t('counterparties.statement.items.leg', { position: item.legPosition })}
          </span>
        </span>
      )
    case 'leg':
      return (
        <span className="text-text-heading">
          {t('counterparties.statement.items.legWhole', { position: item.legPosition })}
        </span>
      )
    case 'other':
      return <span className="text-text-heading">{t('counterparties.statement.items.other')}</span>
    case 'advance':
      return <span className="text-text-heading">{t('counterparties.statement.items.advance')}</span>
  }
}

function SettlementTag({ settlement }: { settlement?: Settlement }) {
  const { t } = useTranslation()
  if (!settlement) return null
  return <Tag tone={SETTLEMENT_TONES[settlement]}>{t(`counterparties.statement.settlement.${settlement}`)}</Tag>
}
